import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";

import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";

import { useAuth, Roles } from "../../providers/AuthContext";
import { getCustomer, updateCustomer, getCustomerThirdPartySystemMapping, addCustomerThirdPartySystemMapping, deleteCustomerThirdPartySystemMapping } from "../../api/Customers";
import { getDiscountsForCustomer, postAssignDiscountToCustomer, postRemoveDiscountToCustomer } from "../../api/Discounts";
import { getAllThirdPartySystems } from "../../api/Resources";
import toast from 'react-hot-toast';
import ConfirmationModal from "../../components/ConfirmationModal";
import DiscountTable from "../../components/customer/DiscountTable";
import TokensTable from "../../components/customer/TokenTable";
import AccessGroupsTable from "../../components/customer/AccessGroupsTable";
import CustomerForm from "../../components/customer/CustomerForm";
import CustomerThirdPartyRef from "../../components/customer/CustomerThirdPartyRef";
import CustomerTariffsTable from "../../components/customer/CustomerTariffsTable";
import { getAccessGroupsForCustomer, addCustomerToAccessGroup, deleteCustomerFromAccessGroup } from "../../api/AccessGroups";
import LoadingSpinner from "../../components/LoadingSpinner";
import { getCustomerTariffs } from "../../api/Tariffs";
import { useTranslation } from "react-i18next";


const EditCustomersPage = () => {
  const { t } = useTranslation();
  const { getAccessToken, hasRole } = useAuth();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null); 
  const [thirdPartySystemMapping, setThirdPartySystemMapping] = useState([]);
  const [thirdPartySystems, setThirdPartySystems] = useState([]);
  const [addModal, setAddModal] = useState(false);

  const [addDiscountModal, setAddDiscountModal] = useState(false);
  const [addDiscountState, setAddDiscountState] = useState([]);
  const [discounts, setDiscounts] = useState([]);

  const [accessGroups, setAccessGroups] = useState([]);
  const [customerTariffs, setCustomerTariffs] = useState([]);

  const [addTPRState, setAddTPRState] = useState({ system: "", id: "" });
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [loadingSystems, setLoadingSystems] = useState(true);
  const [loadingDiscounts, setLoadingDiscounts] = useState(true);
  const [loadingTariffs, setLoadingTariffs] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState(
    query.get("tab") === "tariffs" ? "details" : query.get("tab") ? query.get("tab") : "details"
  );
    const isLoading =
        loadingCustomer ||
        loadingSystems ||
        loadingDiscounts ||
        loadingTariffs
  useEffect(() => {
    const tab = query.get("tab");
    if (tab) {
      setActiveTab(tab === "tariffs" ? "details" : tab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [location.search]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    query.set("tab", activeTab);
    navigate(`${location.pathname}?${query.toString()}`, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [activeTab, location.pathname, navigate]);

  useEffect(() => {
      handleGetCustomer();
    if (hasRole(Roles.Admin)) {
      handleGetCustomerThirdPartyStystemMapping();
      handleGetAllThirdPartySystems();
    } else {
      setLoadingSystems(false);
      setLoadingDiscounts(false);
      setLoadingTariffs(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [id])

  useEffect(() => {
    if (!addModal) {
      setAddTPRState({ system: "", id: "" });
    }
  }, [addModal]);

  const handleGetCustomer = async () => {
    try {
      const accessToken = await getAccessToken();
      const newCustomer = await getCustomer(id, accessToken);
      setCustomer(newCustomer);
      if (hasRole(Roles.Admin)) {
        handeGetDiscounts(newCustomer);
      } else {
        setLoadingDiscounts(false);
      }
      handleGetCustomerTariffs(newCustomer);
      handeGetAccessGroups(newCustomer);
      setLoadingCustomer(false);
    } catch (error) {
      console.error(error);
      setLoadingCustomer(false);
      setLoadingSystems(false);
      setLoadingDiscounts(false);
      setLoadingTariffs(false);
    }
  };

  const handeGetDiscounts = async (c) => {
    try {
      const accessToken = await getAccessToken();
      const discounts = await getDiscountsForCustomer(c.customerPublicId, accessToken);
      const sortedDiscounts = discounts.sort((a, b) => b.minKwh - a.minKwh);
      setDiscounts(sortedDiscounts);
      setLoadingDiscounts(false);
    } catch (error) {
      console.error(error);
      setLoadingDiscounts(false);
    }
  };

  const handleGetCustomerTariffs = async (c) => {
    try {
      const accessToken = await getAccessToken();
      const tariffs = await getCustomerTariffs(c.customerPublicId, accessToken);
      setCustomerTariffs(tariffs || []);
      setLoadingTariffs(false);
    } catch (error) {
      console.error(error);
      toast.error(error.message);
      setLoadingTariffs(false);
    }
  };

  const handeGetAccessGroups = async (c) => {
    try {
      const accessToken = await getAccessToken();
      const ags = await getAccessGroupsForCustomer(c.customerPublicId, accessToken);
      setAccessGroups(ags);
    } catch (error) {
        toast.error(error.message);
    }
  };

  const handleGetCustomerThirdPartyStystemMapping = async () => {
    try {
      const accessToken = await getAccessToken();
      const newTPSM = await getCustomerThirdPartySystemMapping(id, accessToken);
      setThirdPartySystemMapping(newTPSM);
    } catch (error) {
        console.error(error.message);
    }
  };

  const handleGetAllThirdPartySystems = async () => {
    try {
      const accessToken = await getAccessToken();
      const systems = await getAllThirdPartySystems(accessToken);
      setThirdPartySystems(systems);
      setLoadingSystems(false);
    } catch (error) {
      toast.error(t("toast.failedFetch", { entity: t("entity.thirdPartySystems") }), error);
      setLoadingSystems(false);
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({ ...s, isOpen: true, title: t("common.discardChanges"), confirm: () => { navigate("/customers") } }));
    } else {
      navigate("/customers")
    }
  };

  const saveAndExit = () => {
    handleUpdateCustomer();
  };

  const handleUpdateCustomer = async () => {
    const toastId = toast.loading(t("toast.updating", { entity: t("entity.customer") }));
    try {
      const accessToken = await getAccessToken();
        const payload = {
            customerPublicId: customer.customerPublicId,
            name: customer.name,
            organizationNumber: customer.organizationNumber,
            PaymentPublicId: customer.paymentMethod?.paymentMethodPublicId ?? null,
        };
        const updatedCustomer = await updateCustomer(payload, accessToken);
      if (hasRole(Roles.Admin)) {
          await handleSaveTPRef(updatedCustomer);
          await handleSaveDiscounts(updatedCustomer);
      }
      await handleSaveAccessGroups(updatedCustomer);

      handeGetAccessGroups(updatedCustomer);
      if (hasRole(Roles.Admin)) {
          handleGetCustomerThirdPartyStystemMapping();
          handeGetDiscounts(updatedCustomer);
      }
      handleGetCustomerTariffs(updatedCustomer);

      toast.success(t("toast.updated", { entity: t("entity.customer") }), { id: toastId });
      setHasChanges(false);
    } catch (error) {
      // Handle errors during the update process
        toast.error(error.message, { id: toastId });
      console.error(error);
    }
  };

  const handleSaveTPRef = async (c) => {
    const accessToken = await getAccessToken();
    // Hantera third-party-system-mappingar
    const thirdPartySystemPromises = thirdPartySystemMapping.map(async (thirdPartySystem) => {
      if (thirdPartySystem.isNew) {
        // Lägg till nya third-party system mappingar
        thirdPartySystem.customerPublicId = c.customerPublicId;
        return addCustomerThirdPartySystemMapping(thirdPartySystem, accessToken);
      } else if (thirdPartySystem.isDeleted) {
        // Ta bort befintliga third-party system mappingar
        return deleteCustomerThirdPartySystemMapping(
          thirdPartySystem.thirdPartySystemPublicId,
          thirdPartySystem.customerPublicId,
          thirdPartySystem.thirdPartySystemExternalId,
          accessToken
        );
      }
    });
    // Execute all third-party system mapping changes in parallel
    await Promise.all(thirdPartySystemPromises);
  };

  const handleSaveAccessGroups = async () => {
    const accessToken = await getAccessToken();
    // Hantera third-party-system-mappingar
    const agcalls = accessGroups.map(async (accessGroup) => {
      if (accessGroup.isNew) {
        return addCustomerToAccessGroup({
          accessGroupPublicId: accessGroup.accessGroupPublicId,
          customerPublicId: customer.customerPublicId
        }, accessToken);
      } else if (accessGroup.isDeleted) {
        return deleteCustomerFromAccessGroup({
          accessGroupPublicId: accessGroup.accessGroupPublicId,
          customerPublicId: customer.customerPublicId
        }, accessToken);
      }
    });
    // Execute all third-party system mapping changes in parallel
    await Promise.all(agcalls);
  };

  const handleSaveDiscounts = async (c) => {
    const accessToken = await getAccessToken();

    const discountPromises = discounts.map(async (discount) => {
      if (discount.isNew) {
        // Assign the new discount to the customer
        await postAssignDiscountToCustomer(discount?.discountPublicId, c.customerPublicId, accessToken);
      } else if (discount.isDeleted) {
        // Delete existing discount
        await postRemoveDiscountToCustomer(discount?.discountPublicId, c.customerPublicId, accessToken);
      }
    });

    // Execute all discount changes in parallel
    await Promise.all(discountPromises);

    }

    if (isLoading) {
        return (
            <>
                <HeaderBar />
                <div className="flex flex-row w-full">
                    <Sidebar />

                    <main className="page flex items-center justify-center">
                        <LoadingSpinner text={t("common.loadingEntity", { name: t("entity.customer") })} />
                    </main>
                </div>
            </>
        );
    }


  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="page-content">
             <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex ">
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4 ">
                {id !=="0" ? t("pages.editNamed", { name: customer?.name }) : t("pages.createCustomer")}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  {t("common.cancel")}
                </button>
                <button className="btn btn-primary" disabled={!customer?.name || !hasChanges} onClick={() => { saveAndExit() }}>
                  {t("common.save")}
                </button>
              </div>
            </div>

            <div className=" px-4 py-8 shadow-lg rounded-md w-full">
              <div className="border-b border-mute-200 mb-4 gap-4 flex">
                <button
                  className={`px-4 pb-2 ${activeTab === "details"
                    ? "border-b-2 border-black font-bold text-sm text-black"
                    : "text-mute-400 text-sm font-bold"
                    }`}
                  onClick={() => setActiveTab("details")}
                >
                  {t("tabs.details")}
                </button>
                {hasRole(Roles.Admin) && (
                    <button
                        className={`px-4 pb-2 ${activeTab === "discounts"
                                ? "border-b-2 border-black font-bold text-sm text-black"
                                : "text-mute-400 text-sm font-bold"
                            }`}
                        onClick={() => setActiveTab("discounts")}
                    >
                        {t("entity.discounts")}
                    </button>
                )}
                <button
                  className={`px-4 pb-2 ${activeTab === "tokens"
                    ? "border-b-2 border-black font-bold text-sm text-black"
                    : "text-mute-400 text-sm font-bold"
                    }`}
                  onClick={() => setActiveTab("tokens")}
                >
                  {t("entity.tokens")}
                </button>
                <button
                  className={`px-4 pb-2 ${activeTab === "accessGroups"
                    ? "border-b-2 border-black font-bold text-sm text-black"
                    : "text-mute-400 text-sm font-bold"
                    }`}
                  onClick={() => setActiveTab("accessGroups")}
                >
                  {t("entity.accessGroups")}
                </button>
                {hasRole(Roles.Admin) && (
                  <button
                    className={`px-4 pb-2 ${activeTab === "references"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                      }`}
                    onClick={() => setActiveTab("references")}
                  >
                    {t("tabs.references")}
                  </button>
                )}
              </div>
                <div className="flex flex-col w-full gap-4">
                {activeTab === "details" &&
                    <>
                      <CustomerForm customer={customer} setCustomer={setCustomer} setHasChanges={setHasChanges} isEdit={true} />
                      <CustomerTariffsTable tariffs={customerTariffs} canOpenTariff={hasRole(Roles.Admin)} />
                    </>
                }
                {activeTab === "discounts" &&
                  <div className="flex w-full">

                    <DiscountTable
                      addModal={addDiscountModal} setAddModal={setAddDiscountModal}
                      setAddDiscountState={setAddDiscountState} addDiscountState={addDiscountState}
                      discounts={discounts} setDiscounts={setDiscounts}
                      setConfirmState={setConfirmState} setHasChanges={setHasChanges}
                      setAddDiscountModal={setAddDiscountModal}
                    />

                  </div>

                }
                {activeTab === "tokens" &&
                  <div className="flex w-full">

                    <TokensTable customerPublicId={customer?.customerPublicId} />

                  </div>
                }
                {activeTab === "accessGroups" &&
                  <div className="flex w-full">

                    <AccessGroupsTable
                      accessGroups={accessGroups}
                      setAccessGroups={setAccessGroups}
                      setHasChanges={setHasChanges}
                    />

                  </div>
                }
                {hasRole(Roles.Admin) && activeTab === "references" &&
                  <div className="flex w-full">

                    <CustomerThirdPartyRef thirdPartySystems={thirdPartySystems}
                      setThirdPartySystems={setThirdPartySystems}
                      thirdPartySystemMapping={thirdPartySystemMapping}
                      setThirdPartySystemMapping={setThirdPartySystemMapping}
                      addModal={addModal}
                      setAddModal={setAddModal}
                      addTPRState={addTPRState}
                      setAddTPRState={setAddTPRState}
                      confirmState={confirmState}
                      setConfirmState={setConfirmState}
                      setHasChanges={setHasChanges}
                    />

                  </div>
                }
              </div>
            </div>

          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title ? confirmState.title : t("confirm.deleteThirdPartyRef")}
          onClose={() => { setConfirmState((s) => ({ ...s, isOpen: false })) }}
          onConfirm={() => { setConfirmState((s) => ({ ...s, isOpen: false })); confirmState.confirm() }}
        />
      </div>
    </>
  );
};

export default EditCustomersPage;

